import { audibleLanes, emptyMonitoring, type PageRMonitoring } from './page-r-monitor';

export type PlaybackState = 'idle' | 'playing' | 'paused';
export type PlaybackLocation = { pattern: number | null; row: number | null; occurrence: number | null };
export type PlaybackCallbacks = { onState(state: PlaybackState): void; onTime(position: number, duration: number): void; onLocation?(location: PlaybackLocation): void; onError?(error: unknown): void; onEnded(): void };
export type PcmStreamInfo = { sampleRate: number; totalFrames: number };
export type PcmStreamChunk = { pcmBuffer?: ArrayBuffer; laneBuffers?: ArrayBuffer[]; frameCount: number; frameOffset?: number; done: boolean; paused?: boolean; locations?: (PlaybackLocation & { frame: number })[] };

export interface PlatformAdapter {
  chooseFile(accept?: string): Promise<File | null>;
  download(filename: string, bytes: Uint8Array, mime?: string): void;
  playVoice(bytes: Uint8Array, options: { loop?: { start: number; end: number } }, callbacks: PlaybackCallbacks): Promise<void>;
  playPcmStream(info: PcmStreamInfo, pull: () => Promise<PcmStreamChunk>, callbacks: PlaybackCallbacks): Promise<void>;
  pauseAudio(): Promise<void>;
  resumeAudio(): Promise<void>;
  stopAudio(resetPosition?: boolean): void;
  setPageRMonitoring?(state: PageRMonitoring): void;
}

export function decodeBase64(value: string): Uint8Array<ArrayBuffer> {
  const binary = atob(value); const result = new Uint8Array(binary.length);
  for (let index = 0; index < binary.length; index++) result[index] = binary.charCodeAt(index);
  return result;
}

export class BrowserPlatformAdapter implements PlatformAdapter {
  private audio?: AudioContext; private sources = new Set<AudioBufferSourceNode>();
  private callbacks?: PlaybackCallbacks; private animation?: number; private generation = 0;
  private state: PlaybackState = 'idle'; private duration = 0; private position = 0;
  private startedAt = 0; private scheduledUntil = 0; private streamDone = false; private streamPumping = false;
  private monitoring = emptyMonitoring(); private laneGains: GainNode[] = [];
  setPageRMonitoring(state: PageRMonitoring) {
    this.monitoring = { muted: [...state.muted], soloed: [...state.soloed] };
    const mask = audibleLanes(this.monitoring);
    // Gains affect already scheduled buffers, without re-rendering or seeking.
    this.laneGains.forEach((gain, lane) => {
      gain.gain.setValueAtTime(mask[lane] ? 1 : 0, this.audio?.currentTime ?? 0);
    });
  }

  chooseFile(accept = '*/*'): Promise<File | null> {
    return new Promise((resolve) => {
      const input = document.createElement('input'); input.type = 'file'; input.accept = accept;
      input.onchange = () => resolve(input.files?.[0] ?? null); input.click();
    });
  }

  download(filename: string, bytes: Uint8Array, mime = 'application/octet-stream') {
    const copy = Uint8Array.from(bytes).buffer;
    const blob = new Blob([copy], { type: mime }); const url = URL.createObjectURL(blob);
    const link = document.createElement('a'); link.href = url; link.download = filename; link.click();
    setTimeout(() => URL.revokeObjectURL(url), 1000);
  }

  private newSession(callbacks: PlaybackCallbacks, duration: number) {
    this.stopAudio(true); this.generation += 1; this.audio = new AudioContext(); this.callbacks = callbacks;
    this.duration = duration; this.position = 0; this.state = 'playing';
    callbacks.onTime(0, duration); callbacks.onState('playing'); return this.generation;
  }

  private cleanupAudio() {
    if (this.animation !== undefined) cancelAnimationFrame(this.animation); this.animation = undefined;
    for (const source of this.sources) { try { source.stop(); } catch { /* already ended */ } }
    this.sources.clear(); const audio = this.audio; this.audio = undefined;
    this.laneGains.forEach(gain => gain.disconnect()); this.laneGains = [];
    if (audio) void audio.close().catch(() => undefined); this.streamPumping = false; this.streamDone = false;
  }

  private finish(position = this.duration) {
    const callbacks = this.callbacks; this.position = position; this.state = 'idle';
    callbacks?.onTime(position, this.duration); callbacks?.onState('idle'); callbacks?.onEnded();
    this.cleanupAudio(); this.callbacks = undefined;
  }

  async playVoice(bytes: Uint8Array, options: { loop?: { start: number; end: number } }, callbacks: PlaybackCallbacks) {
    const requestGeneration = this.generation;
    const decoder = new AudioContext(); const decoded = await decoder.decodeAudioData(Uint8Array.from(bytes).buffer); await decoder.close();
    if (requestGeneration !== this.generation) return;
    const generation = this.newSession(callbacks, decoded.duration); const audio = this.audio!;
    const source = audio.createBufferSource(); source.buffer = decoded;
    if (options.loop && options.loop.end > options.loop.start) { source.loop = true; source.loopStart = options.loop.start; source.loopEnd = options.loop.end; }
    source.connect(audio.destination); this.sources.add(source); this.startedAt = audio.currentTime;
    source.onended = () => { this.sources.delete(source); if (generation === this.generation && !source.loop && this.state !== 'idle') this.finish(decoded.duration); };
    source.start();
    const tick = () => {
      if (generation !== this.generation || !this.audio || this.state === 'idle') return;
      if (this.state === 'playing') {
        const elapsed = Math.max(0, this.audio.currentTime - this.startedAt); const loop = options.loop;
        this.position = loop && elapsed >= loop.start ? loop.start + ((elapsed - loop.start) % (loop.end - loop.start)) : Math.min(elapsed, decoded.duration);
        callbacks.onTime(this.position, decoded.duration);
      }
      this.animation = requestAnimationFrame(tick);
    };
    this.animation = requestAnimationFrame(tick);
  }

  async playPcmStream(info: PcmStreamInfo, pull: () => Promise<PcmStreamChunk>, callbacks: PlaybackCallbacks) {
    const generation = this.newSession(callbacks, info.totalFrames / info.sampleRate); const audio = this.audio!; await audio.suspend();
    if (generation !== this.generation) return;
    this.laneGains = Array.from({ length: 8 }, () => { const gain = audio.createGain(); gain.connect(audio.destination); return gain; });
    this.setPageRMonitoring(this.monitoring);
    this.scheduledUntil = audio.currentTime + 0.04;
    let renderedFrames = 0;
    const locations: { time: number; location: PlaybackLocation }[] = [];
    const segments: { start: number; end: number; frame: number }[] = [];
    const fail = (error: unknown) => { if (generation === this.generation) { callbacks.onError?.(error); this.finish(this.position); } };
    const pump = async () => {
      if (generation !== this.generation || this.streamPumping || this.streamDone || this.state !== 'playing') return;
      this.streamPumping = true;
      try {
        // Backpressure: never schedule more than 0.50 seconds ahead.
        while (generation === this.generation && this.state === 'playing' && !this.streamDone && this.scheduledUntil - audio.currentTime < 0.50) {
          const chunk = await pull();
          if (generation !== this.generation || this.audio !== audio) return;
          if (chunk.done) { this.streamDone = true; break; }
          if ((!chunk.pcmBuffer && !chunk.laneBuffers) || !chunk.frameCount) break;
          // Pause can occur during a worker pull: schedule that already-returned
          // block on the suspended clock, but do not pull another until resume.
          this.scheduledUntil = Math.max(this.scheduledUntil, audio.currentTime + 0.02);
          const offset = chunk.frameOffset ?? renderedFrames;
          for (const location of chunk.locations ?? []) locations.push({ time: this.scheduledUntil + (location.frame - offset) / info.sampleRate, location });
          segments.push({ start: this.scheduledUntil, end: this.scheduledUntil + chunk.frameCount / info.sampleRate, frame: offset });
          const buffers = chunk.laneBuffers ?? [chunk.pcmBuffer!];
          if (chunk.laneBuffers && buffers.length !== 8) throw new Error('PAGE R preview requires eight keyboard lane buffers');
          buffers.forEach((pcm, lane) => {
            const samples = new Float32Array(pcm);
            if (samples.length !== chunk.frameCount) throw new Error('PAGE R PCM frame count mismatch');
            const buffer = audio.createBuffer(1, chunk.frameCount, info.sampleRate);
            buffer.copyToChannel(samples, 0); const source = audio.createBufferSource(); source.buffer = buffer;
            source.connect(chunk.laneBuffers ? this.laneGains[lane] : audio.destination);
            source.start(this.scheduledUntil); this.sources.add(source); source.onended = () => this.sources.delete(source);
          });
          this.scheduledUntil += chunk.frameCount / info.sampleRate;
          renderedFrames = offset + chunk.frameCount;
        }
      } finally { if (generation === this.generation) this.streamPumping = false; }
      if (generation === this.generation && audio.state === 'suspended' && this.state === 'playing' && (this.streamDone || this.scheduledUntil > audio.currentTime + 0.10)) await audio.resume();
    };
    await pump();
    const tick = () => {
      if (generation !== this.generation || !this.audio || this.state === 'idle') return;
      if (this.state === 'playing') {
        while (segments.length && segments[0].end <= audio.currentTime) { const segment = segments.shift()!; this.position = (segment.frame / info.sampleRate) + (segment.end - segment.start); }
        const segment = segments[0];
        if (segment && audio.currentTime >= segment.start) this.position = segment.frame / info.sampleRate + Math.min(audio.currentTime - segment.start, segment.end - segment.start);
        while (locations.length && locations[0].time <= audio.currentTime) callbacks.onLocation?.(locations.shift()!.location);
        callbacks.onTime(this.position, this.duration); void pump().catch(fail);
        if (this.streamDone && this.audio.currentTime >= this.scheduledUntil) { this.finish(this.duration); return; }
      }
      this.animation = requestAnimationFrame(tick);
    };
    this.animation = requestAnimationFrame(tick);
  }

  async pauseAudio() { if (this.state === 'playing' && this.audio) { await this.audio.suspend(); this.state = 'paused'; this.callbacks?.onState('paused'); } }
  async resumeAudio() { if (this.state === 'paused' && this.audio) { await this.audio.resume(); this.state = 'playing'; this.callbacks?.onState('playing'); } }
  stopAudio(resetPosition = true) {
    this.generation += 1;
    if (this.state !== 'idle') { this.state = 'idle'; this.callbacks?.onTime(resetPosition ? 0 : this.position, this.duration); this.callbacks?.onState('idle'); }
    this.cleanupAudio(); this.callbacks = undefined; if (resetPosition) this.position = 0;
  }
}
